import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';

// Story 79.2 — Stakeholder coverage panel for AccountDetailPage.
// Shows buying-group role fill state and composite ABM score.

interface AccountScore {
  accountId: string;
  score: number;
  roleCoveragePct: number;
  rolesPresent: string[];
  missingRoles: string[];
  inTargetList: boolean;
  leadSignalPts: number;
}

const ALL_ROLES = [
  'DECISION_MAKER',
  'ECONOMIC_BUYER',
  'TECHNICAL_BUYER',
  'CHAMPION',
  'INFLUENCER',
] as const;

function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 70
      ? 'bg-emerald-900/40 text-emerald-200'
      : score >= 40
        ? 'bg-amber-900/40 text-amber-200'
        : 'bg-zinc-800/60 text-muted-foreground';
  return (
    <span className={`rounded-full px-3 py-0.5 text-sm font-semibold tabular-nums ${color}`}>
      {score}/100
    </span>
  );
}

export function StakeholderCoveragePanel({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const [score, setScore] = useState<AccountScore | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<AccountScore>(`/accounts/${accountId}/abm-score`);
      setScore(data);
    } catch {
      setScore(null);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (!score) return null;

  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('abmScore.coverage')}
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{t('abmScore.score')}</span>
          <ScoreBadge score={score.score} />
        </div>
      </div>

      {/* Role coverage bar */}
      <div>
        <div className="mb-2 flex justify-between text-xs text-muted-foreground">
          <span>{t('abmScore.coveragePct', { pct: Math.round(score.roleCoveragePct) })}</span>
          <span className="tabular-nums">{score.rolesPresent.length}/{ALL_ROLES.length}</span>
        </div>
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${score.roleCoveragePct}%` }}
          />
        </div>
      </div>

      {/* Role chips */}
      <div className="flex flex-wrap gap-1.5">
        {ALL_ROLES.map((role) => {
          const filled = score.rolesPresent.includes(role);
          return (
            <span
              key={role}
              className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                filled
                  ? 'bg-primary/10 text-primary'
                  : 'bg-muted text-muted-foreground line-through'
              }`}
            >
              {t(`buyingRole.${role}`)}
            </span>
          );
        })}
      </div>

      {/* Signal breakdown */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1.5">
          <span className="text-muted-foreground">{t('abmScore.inTargetList')}</span>
          <span className={score.inTargetList ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}>
            {score.inTargetList ? '+20 pts' : '0 pts'}
          </span>
        </div>
        <div className="flex items-center justify-between rounded-md bg-muted/30 px-2 py-1.5">
          <span className="text-muted-foreground">{t('abmScore.leadSignal')}</span>
          <span className="tabular-nums font-medium">+{score.leadSignalPts} pts</span>
        </div>
      </div>
    </div>
  );
}
